import { useEffect } from "react";
import ReactPhoneInput from "react-phone-input-2";
import type { CountryData, PhoneInputProps } from "react-phone-input-2";
import { useTheme } from "@mui/material";
import useTranslation from "next-translate/useTranslation";
import { useFormikContext } from "formik";
import type { ComponentType, CSSProperties, FormEvent } from "react";
import type { FormContextValue } from "@/context";
import { useFormContext } from "@/hooks/useFormContext";
import { FormSkeleton } from "../../skeleton";
import "react-phone-input-2/lib/material.css";

type FormValues = Record<string, unknown>;
const PhoneInput = ReactPhoneInput as ComponentType<PhoneInputProps & { required?: boolean }>;

export interface PhoneNumberProps {
  loading?: boolean;
  name?: string;
  value?: string | null;
  label?: string;
  country?: string;
  onChange?: (phone: string, countryData: CountryData | object) => void;
  variant?: string;
  sx?: CSSProperties;
  error?: string;
  placeholder?: string;
  required?: boolean;
}

export function PhoneNumber({
  loading,
  name,
  value,
  label = "",
  country,
  onChange,
  variant,
  sx,
  error,
  placeholder,
  required,
}: PhoneNumberProps) {
  const { formContext } = (useFormContext() ?? {}) as FormContextValue & {
    formContext?: FormContextValue;
  };
  const formik = useFormikContext<FormValues>();
  const fieldName = name as string;
  const { t } = useTranslation();
  const theme = useTheme();

  useEffect(() => {
    formik.setFieldError(fieldName, error);
  }, [error, required, value, name, formik, t]);

  const handlePhoneChange = (phone: string, countryData: CountryData | object) => {
    onChange && onChange(phone, countryData);
    formik.setFieldValue(fieldName, { value: phone, country: countryData });
  };

  return (
    <FormSkeleton loading={loading ?? formContext?.loading}>
      <PhoneInput
        placeholder={placeholder}
        specialLabel={label}
        country={country}
        value={value}
        required={required}
        onChange={handlePhoneChange}
        inputProps={{
          name: "phone",
          required,
          style: {
            backgroundColor: variant === "filled" ? theme.palette.background.main : null,
            border: variant === "filled" ? "none" : null,
            width: "100%",
            zIndex: 1000,
            ...sx,
          },
          onInvalid: (e: FormEvent<HTMLInputElement>) =>
            e.currentTarget.setCustomValidity("Lütfen bu alanı doldurun."),
          onInput: (e: FormEvent<HTMLInputElement>) => e.currentTarget.setCustomValidity(""),
        }}
        enableSearch
        searchPlaceholder="Ülke ara..."
        searchNotFound="Ülke bulunamadı"
        localization={{ Turkey: "Türkiye" }}
      />
    </FormSkeleton>
  );
}
